import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  Music, Headphones, Clock, List, BookOpen, X, RefreshCw,
  Play, Pause, SkipForward, Square, Volume2, ChevronDown, ChevronRight, LogIn, Trophy, ArrowUp, Search, Sparkles, Star, PlusCircle, ExternalLink, Copy, Check
} from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#5865F2', '#D946EF', '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#06B6D4'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="stat-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 font-[Manrope]">{label}</p>
          <p className="text-4xl font-mono-stats font-bold text-white">{value ?? '—'}</p>
          {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: accent + '33' }}>
          <Icon size={20} style={{ color: accent }} />
        </div>
      </div>
    </motion.div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}:${String(m % 60).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ─── Join Voice Channel ──────────────────────────────────────────────────────

function JoinChannelForm({ guildId }) {
  const [channels, setChannels] = useState(null);
  const [selected, setSelected] = useState('');
  const [currentChannelId, setCurrentChannelId] = useState(null);
  const [joining, setJoining] = useState(false);
  const [status, setStatus] = useState(null); // null | {ok, msg}

  const fetchChannels = useCallback(async () => {
    try {
      const token = localStorage.getItem('discord_token');
      const { data } = await axios.get(`${API}/api/music/voice-channels/${guildId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChannels(data.channels || []);
      setCurrentChannelId(data.current_channel_id || null);
      setSelected(prev => prev || data.current_channel_id || data.channels?.[0]?.id || '');
    } catch (e) {
      setChannels([]);
    }
  }, [guildId]);

  useEffect(() => { fetchChannels(); }, [fetchChannels]);

  const handleJoin = async () => {
    if (!selected) return;
    setJoining(true);
    setStatus(null);
    try {
      const token = localStorage.getItem('discord_token');
      const { data } = await axios.post(
        `${API}/api/music/join`,
        { guild_id: guildId, channel_id: selected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentChannelId(data.channel_id);
      setStatus({ ok: true, msg: `Joined "${data.channel_name}"` });
      setTimeout(() => setStatus(null), 4000);
    } catch (err) {
      setStatus({ ok: false, msg: err.response?.data?.detail || 'Could not join that channel.' });
      setTimeout(() => setStatus(null), 5000);
    } finally {
      setJoining(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden mb-6"
      data-testid="join-channel-form"
    >
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[var(--border)]">
        <LogIn size={15} className="text-[#5865F2]" />
        <span className="text-sm font-semibold text-white font-[Outfit]">Join Voice Channel</span>
        <span className="text-xs text-zinc-600 font-[Manrope] ml-1">— from the dashboard</span>
      </div>

      <div className="p-4">
        {channels === null ? (
          <p className="text-zinc-600 text-sm font-[Manrope]">Loading channels...</p>
        ) : channels.length === 0 ? (
          <p className="text-zinc-600 text-sm font-[Manrope]">No voice channels found in this server.</p>
        ) : (
          <div className="flex gap-2">
            <select
              value={selected}
              onChange={e => setSelected(e.target.value)}
              data-testid="join-channel-select"
              className="flex-1 bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#5865F2]/50 focus:bg-white/8 transition-all font-[Manrope]"
            >
              {channels.map(ch => (
                <option key={ch.id} value={ch.id} className="bg-[var(--bg-surface)]">
                  {ch.name} {ch.member_count > 0 ? `(${ch.member_count} listening)` : ''}
                  {ch.id === currentChannelId ? ' • current' : ''}
                </option>
              ))}
            </select>
            <button
              onClick={handleJoin}
              disabled={joining || !selected}
              data-testid="join-channel-submit"
              className="px-5 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all font-[Outfit] flex items-center gap-2 flex-shrink-0"
            >
              {joining
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Joining...</>
                : <><LogIn size={14} />Join</>
              }
            </button>
          </div>
        )}

        <AnimatePresence>
          {status && (
            <motion.div
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className={`mt-3 p-3 rounded-xl ${
                status.ok
                  ? 'bg-emerald-500/10 border border-emerald-500/20'
                  : 'bg-red-500/10 border border-red-500/20'
              }`}
              data-testid="join-channel-feedback"
            >
              <p className={`text-sm font-semibold font-[Outfit] ${status.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                {status.msg}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}


// ─── Song Request Form ───────────────────────────────────────────────────────

function SongRequestForm({ guildId }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState(null); // null | 'loading' | {ok, msg, title, thumbnail, action}
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const [aiMode, setAiMode] = useState(false);
  const [vibe, setVibe] = useState('');
  const [aiStatus, setAiStatus] = useState(null); // null | 'loading' | {ok, msg/songs}

  const handleAiSubmit = async (e) => {
    e.preventDefault();
    if (!vibe.trim()) return;
    setAiStatus('loading');
    try {
      const token = localStorage.getItem('discord_token');
      const { data } = await axios.post(
        `${API}/api/music/ai-playlist`,
        { vibe: vibe.trim(), guild_id: guildId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiStatus({ ok: true, songs: data.songs, skipped: data.skipped });
      setVibe('');
      setTimeout(() => setAiStatus(null), 6000);
    } catch (err) {
      setAiStatus({ ok: false, msg: err.response?.data?.detail || 'AI playlist generation failed.' });
      setTimeout(() => setAiStatus(null), 5000);
    }
  };

  const handleQueryChange = (value) => {
    setQuery(value);
    setShowSuggestions(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim() || value.trim().startsWith('http')) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/music/suggest`, {
          params: { q: value.trim() },
          headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` },
        });
        setSuggestions(data.suggestions || []);
      } catch {
        setSuggestions([]);
      }
    }, 300);
  };

  const pickSuggestion = (s) => {
    setQuery(s);
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setShowSuggestions(false);
    setSuggestions([]);
    setStatus('loading');
    try {
      const token = localStorage.getItem('discord_token');
      const { data } = await axios.post(
        `${API}/api/music/request`,
        { query: query.trim(), guild_id: guildId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus({ ok: true, action: data.action, title: data.title, thumbnail: data.thumbnail, count: data.count });
      setQuery('');
      setTimeout(() => setStatus(null), 4000);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Request failed. Is the bot in a voice channel?';
      setStatus({ ok: false, msg });
      setTimeout(() => setStatus(null), 5000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl mb-6"
      data-testid="song-request-form"
    >
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[var(--border)]">
        <Volume2 size={15} className="text-[#5865F2]" />
        <span className="text-sm font-semibold text-white font-[Outfit]">Request a Song</span>
        <span className="text-xs text-zinc-600 font-[Manrope] ml-1">— from the dashboard</span>
        <button
          type="button"
          onClick={() => setAiMode(v => !v)}
          data-testid="ai-playlist-toggle"
          className={`ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold font-[Outfit] transition-colors ${
            aiMode ? 'bg-[#5865F2] text-white' : 'bg-[var(--bg-hover)] text-zinc-400 hover:bg-white/10'
          }`}
        >
          <Sparkles size={12} /> AI Playlist
        </button>
      </div>

      <div className="p-4">
        {aiMode ? (
          <form onSubmit={handleAiSubmit} className="flex gap-2">
            <input
              value={vibe}
              onChange={e => setVibe(e.target.value)}
              placeholder="Describe a vibe, e.g. 'chill lo-fi for studying'..."
              className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 focus:bg-white/8 transition-all font-[Manrope]"
              data-testid="ai-playlist-input"
              disabled={aiStatus === 'loading'}
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={aiStatus === 'loading' || !vibe.trim()}
              data-testid="ai-playlist-submit"
              className="px-5 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all font-[Outfit] flex items-center gap-2 flex-shrink-0"
            >
              {aiStatus === 'loading'
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Generating...</>
                : <><Sparkles size={14} />Generate</>
              }
            </button>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              value={query}
              onChange={e => handleQueryChange(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="Song name, artist, YouTube/YouTube Music URL, or playlist link..."
              className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 focus:bg-white/8 transition-all font-[Manrope]"
              data-testid="song-request-input"
              disabled={status === 'loading'}
              autoComplete="off"
            />
            <AnimatePresence>
              {showSuggestions && suggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="absolute left-0 right-0 mt-1.5 z-50 bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-xl shadow-xl overflow-hidden"
                  data-testid="song-suggestions"
                >
                  {suggestions.map((s, i) => (
                    <button
                      type="button"
                      key={i}
                      onMouseDown={() => pickSuggestion(s)}
                      data-testid={`song-suggestion-${i}`}
                      className="w-full text-left px-4 py-2 text-sm text-zinc-300 hover:bg-[var(--bg-hover)] hover:text-white transition-colors truncate font-[Manrope]"
                    >
                      {s}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button
            type="submit"
            disabled={status === 'loading' || !query.trim()}
            data-testid="song-request-submit"
            className="px-5 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all font-[Outfit] flex items-center gap-2 flex-shrink-0"
          >
            {status === 'loading'
              ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Searching...</>
              : <><Play size={14} fill="currentColor" />Request</>
            }
          </button>
        </form>
        )}

        <AnimatePresence>
          {status && status !== 'loading' && (
            <motion.div
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className={`mt-3 flex items-start gap-3 p-3 rounded-xl ${
                status.ok
                  ? 'bg-emerald-500/10 border border-emerald-500/20'
                  : 'bg-red-500/10 border border-red-500/20'
              }`}
              data-testid="song-request-feedback"
            >
              {status.ok ? (
                status.action === 'playlist_queued' ? (
                  <div>
                    <p className="text-emerald-400 text-sm font-semibold font-[Outfit]">Playlist queued!</p>
                    <p className="text-zinc-400 text-xs font-[Manrope] mt-0.5">Added {status.count} songs to the queue</p>
                  </div>
                ) : (
                  <>
                    {status.thumbnail && <img src={status.thumbnail} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />}
                    <div>
                      <p className="text-emerald-400 text-sm font-semibold font-[Outfit]">
                        {status.action === 'playing' ? 'Now playing!' : 'Added to queue!'}
                      </p>
                      <p className="text-zinc-400 text-xs font-[Manrope] mt-0.5 truncate max-w-xs">{status.title}</p>
                    </div>
                  </>
                )
              ) : (
                <div>
                  <p className="text-red-400 text-sm font-semibold font-[Outfit]">Could not request song</p>
                  <p className="text-zinc-400 text-xs font-[Manrope] mt-0.5">{status.msg}</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {aiStatus && aiStatus !== 'loading' && (
            <motion.div
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className={`mt-3 p-3 rounded-xl ${
                aiStatus.ok
                  ? 'bg-emerald-500/10 border border-emerald-500/20'
                  : 'bg-red-500/10 border border-red-500/20'
              }`}
              data-testid="ai-playlist-feedback"
            >
              {aiStatus.ok ? (
                <div>
                  <p className="text-emerald-400 text-sm font-semibold font-[Outfit]">Playlist generated!</p>
                  <p className="text-zinc-400 text-xs font-[Manrope] mt-1 space-y-0.5">
                    {aiStatus.songs.map((s, i) => <span key={i} className="block truncate">• {s}</span>)}
                  </p>
                  {aiStatus.skipped > 0 && (
                    <p className="text-zinc-500 text-xs font-[Manrope] mt-1">{aiStatus.skipped} songs skipped (queue full)</p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-red-400 text-sm font-semibold font-[Outfit]">Could not generate playlist</p>
                  <p className="text-zinc-400 text-xs font-[Manrope] mt-0.5">{aiStatus.msg}</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <p className="text-zinc-700 text-xs font-[Manrope] mt-3">
          Bot must be active in a voice channel — use <code className="text-[#5865F2] bg-[#5865F2]/10 px-1 rounded">/play</code> in Discord, or join one from the panel above.
        </p>
      </div>
    </motion.div>
  );
}



function Waveform({ active, paused }) {
  const canvasRef = useRef(null);
  const phaseRef = useRef(0);
  const barsRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || 64;
    const cssHeight = canvas.clientHeight || 32;
    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    ctx.scale(dpr, dpr);

    const barCount = 9;
    if (!barsRef.current) {
      barsRef.current = Array.from({ length: barCount }, () => Math.random());
    }
    const colors = ['#5865F2', '#D946EF', '#5865F2', '#10B981', '#5865F2', '#D946EF', '#5865F2', '#10B981', '#5865F2'];
    const barWidth = cssWidth / (barCount * 1.8);
    const gap = barWidth * 0.8;

    let frameId;
    const draw = () => {
      ctx.clearRect(0, 0, cssWidth, cssHeight);
      phaseRef.current += active && !paused ? 0.18 : 0.03;
      for (let i = 0; i < barCount; i++) {
        const seed = barsRef.current[i];
        let amplitude;
        if (active && !paused) {
          amplitude = 0.35 + 0.65 * Math.abs(Math.sin(phaseRef.current * (1 + seed) + i));
        } else if (active && paused) {
          amplitude = 0.25 + seed * 0.1;
        } else {
          amplitude = 0.12 + 0.06 * Math.abs(Math.sin(phaseRef.current + i));
        }
        const barHeight = Math.max(2, amplitude * cssHeight);
        const x = i * (barWidth + gap);
        const y = (cssHeight - barHeight) / 2;
        ctx.fillStyle = colors[i % colors.length];
        ctx.globalAlpha = active && !paused ? 1 : 0.45;
        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2);
          ctx.fill();
        } else {
          ctx.fillRect(x, y, barWidth, barHeight);
        }
      }
      ctx.globalAlpha = 1;
      frameId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frameId);
  }, [active, paused]);

  return <canvas ref={canvasRef} className="w-16 h-8" data-testid="hero-waveform" />;
}

function QueueViewer() {
  const [nowPlaying, setNowPlaying] = useState(null);
  const [queueData, setQueueData] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [movingTop, setMovingTop] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const activeGuild = nowPlaying?.playing?.[0] || null;

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const { data: np } = await axios.get(`${API}/api/music/now-playing`);
      setNowPlaying(np);
      const guild = np?.playing?.[0];
      if (guild) {
        const { data: q } = await axios.get(`${API}/api/music/queue/${guild.guild_id}`);
        setQueueData(q);
      } else {
        setQueueData(null);
      }
      setLastUpdated(new Date());
    } catch (e) {
      console.warn('Queue fetch failed:', e.message);
    } finally {
      if (showRefresh) setTimeout(() => setRefreshing(false), 400);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const iv = setInterval(() => fetchData(), 5000);
    return () => clearInterval(iv);
  }, [fetchData]);

  const handleRemove = async (idx) => {
    if (!activeGuild) return;
    setRemoving(idx);
    try {
      await axios.delete(`${API}/api/music/queue/${activeGuild.guild_id}/${idx}`);
      setQueueData(prev => prev ? { ...prev, queue: prev.queue.filter((_, i) => i !== idx) } : prev);
    } catch (e) {
      console.warn('Remove failed:', e);
    } finally {
      setRemoving(null);
    }
  };

  const handleMoveToTop = async (idx) => {
    if (!activeGuild || idx === 0) return;
    setMovingTop(idx);
    try {
      await axios.post(`${API}/api/music/queue/${activeGuild.guild_id}/${idx}/move-to-top`);
      setQueueData(prev => {
        if (!prev) return prev;
        const q = [...prev.queue];
        const [item] = q.splice(idx, 1);
        q.unshift(item);
        return { ...prev, queue: q };
      });
    } catch (e) {
      console.warn('Move to top failed:', e);
    } finally {
      setMovingTop(null);
    }
  };

  const handleControl = async (action, extra = {}) => {
    if (!activeGuild) return;
    try {
      await axios.post(`${API}/api/music/control/${activeGuild.guild_id}`, { action, ...extra });
      setTimeout(() => fetchData(), 400);
    } catch (e) {
      console.warn('Control failed:', e);
    }
  };

  const isActive = activeGuild?.is_playing && !activeGuild?.is_paused;
  const isPaused = activeGuild?.is_paused;
  const queue = queueData?.queue || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden"
      data-testid="queue-viewer"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border)]">
        <div className="flex items-center gap-2.5">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${activeGuild ? 'bg-emerald-400 pulse-online' : 'bg-zinc-600'}`} />
          <div className="flex items-center gap-1.5">
            <List size={15} className="text-[#5865F2]" />
            <span className="text-sm font-semibold text-white font-[Outfit]">Song Queue</span>
          </div>
          {activeGuild && (
            <span className="text-xs text-zinc-500 font-[Manrope]">— {activeGuild.guild_name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-zinc-700 font-mono-stats hidden sm:block">
              {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            data-testid="queue-viewer-refresh"
            onClick={() => fetchData(true)}
            className="p-1.5 rounded-lg hover:bg-[var(--bg-hover)] text-zinc-500 hover:text-zinc-300 transition-all"
            title="Refresh"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {!activeGuild ? (
          <div className="flex items-center gap-4 py-4" data-testid="queue-empty-state">
            <div className="w-12 h-12 rounded-xl bg-[var(--bg-hover)] border border-[var(--border)] flex items-center justify-center flex-shrink-0">
              <List size={20} className="text-zinc-600" />
            </div>
            <div>
              <p className="text-zinc-300 font-semibold font-[Outfit] text-sm mb-0.5">Queue is empty</p>
              <p className="text-zinc-600 text-xs font-[Manrope]">
                Use <code className="text-[#5865F2] bg-[#5865F2]/10 px-1 rounded">/play</code> in Discord to start the queue.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="queue-content">
            {/* Now Playing row */}
            <div
              className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-[#5865F2]/10 to-transparent border border-[#5865F2]/15"
              data-testid="queue-now-playing"
            >
              {activeGuild.thumbnail ? (
                <img src={activeGuild.thumbnail} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-[#5865F2]/20 flex items-center justify-center flex-shrink-0">
                  <Music size={16} className="text-[#5865F2]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold font-[Outfit] truncate">{activeGuild.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    isPaused ? 'bg-amber-500/15 text-amber-400' :
                    isActive ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-700/50 text-zinc-400'
                  }`}>
                    {isPaused ? 'Paused' : isActive ? 'Now Playing' : 'Idle'}
                  </span>
                  {activeGuild.duration > 0 && (
                    <span className="text-zinc-600 text-xs font-mono-stats">{formatDuration(activeGuild.duration)}</span>
                  )}
                </div>
              </div>
              {/* Quick controls */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  data-testid="queue-control-pause-resume"
                  onClick={() => handleControl(isPaused ? 'resume' : 'pause')}
                  className="p-1.5 rounded-lg bg-[#5865F2]/15 hover:bg-[#5865F2]/30 text-[#5865F2] transition-all"
                  title={isPaused ? 'Resume' : 'Pause'}
                >
                  {isPaused ? <Play size={13} /> : <Pause size={13} />}
                </button>
                <button
                  data-testid="queue-control-skip"
                  onClick={() => handleControl('skip')}
                  className="p-1.5 rounded-lg bg-[var(--bg-hover)] hover:bg-white/10 text-zinc-400 hover:text-white transition-all"
                  title="Skip"
                >
                  <SkipForward size={13} />
                </button>
                <button
                  data-testid="queue-control-stop"
                  onClick={() => handleControl('stop')}
                  className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                  title="Stop"
                >
                  <Square size={13} />
                </button>
              </div>
            </div>

            {/* Queue list */}
            {queue.length === 0 ? (
              <p className="text-zinc-600 text-xs text-center py-3 font-[Manrope]">No songs queued up next</p>
            ) : (
              <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="queue-song-list">
                <p className="text-xs text-zinc-500 uppercase tracking-widest font-semibold font-[Manrope] mb-2 px-1">
                  Up Next — {queue.length} song{queue.length !== 1 ? 's' : ''}
                </p>
                {queue.map((song, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 group px-2 py-2 rounded-lg hover:bg-white/4 transition-colors"
                    data-testid={`queue-item-${i}`}
                  >
                    <span className="text-zinc-700 text-xs font-mono-stats w-5 flex-shrink-0 text-right">{i + 1}</span>
                    <div className="w-7 h-7 rounded-lg bg-[var(--bg-hover)] flex items-center justify-center flex-shrink-0">
                      <Music size={12} className="text-zinc-500" />
                    </div>
                    <p className="text-zinc-300 text-xs truncate flex-1 font-[Manrope]">{song.title || 'Unknown'}</p>
                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
                      {i > 0 && (
                        <button
                          onClick={() => handleMoveToTop(i)}
                          disabled={movingTop === i}
                          className="p-1 rounded text-zinc-600 hover:text-[#5865F2] transition-all"
                          data-testid={`move-top-queue-item-${i}`}
                          title="Move to top"
                        >
                          {movingTop === i
                            ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                            : <ArrowUp size={12} />
                          }
                        </button>
                      )}
                      <button
                        onClick={() => handleRemove(i)}
                        disabled={removing === i}
                        className="p-1 rounded text-zinc-600 hover:text-red-400 transition-all"
                        data-testid={`remove-queue-item-${i}`}
                        title="Remove"
                      >
                        {removing === i
                          ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                          : <X size={12} />
                        }
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-5 py-2 border-t border-[var(--border)]">
        <span className="text-xs text-zinc-700 font-[Manrope]">Auto-refreshes every 5s</span>
      </div>
    </motion.div>
  );
}

// ─── Playlist Browser ─────────────────────────────────────────────────────────

function PlaylistBrowser({ guildId }) {
  const [playlists, setPlaylists] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!guildId) return;
    axios.get(`${API}/api/playlists/${guildId}`)
      .then(r => setPlaylists(r.data.playlists || []))
      .catch(console.warn);
  }, [guildId]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden"
      data-testid="playlist-browser"
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[var(--border)]">
        <BookOpen size={15} className="text-[#D946EF]" />
        <span className="text-sm font-semibold text-white font-[Outfit]">Saved Playlists</span>
        {playlists && (
          <span className="ml-auto text-xs text-zinc-600 font-mono-stats">{playlists.length} saved</span>
        )}
      </div>

      <div className="p-4" data-testid="playlist-list">
        {!playlists ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-[var(--bg-hover)] rounded-xl animate-pulse" />
            ))}
          </div>
        ) : playlists.length === 0 ? (
          <div className="flex items-center gap-4 py-4" data-testid="playlists-empty">
            <div className="w-12 h-12 rounded-xl bg-[var(--bg-hover)] border border-[var(--border)] flex items-center justify-center flex-shrink-0">
              <BookOpen size={20} className="text-zinc-600" />
            </div>
            <div>
              <p className="text-zinc-300 font-semibold font-[Outfit] text-sm mb-0.5">No playlists saved</p>
              <p className="text-zinc-600 text-xs font-[Manrope]">
                Use <code className="text-[#D946EF] bg-[#D946EF]/10 px-1 rounded">/playlist save</code> in Discord.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="playlists-items">
            {playlists.map((pl, i) => {
              const isOpen = expanded === i;
              return (
                <div key={i} className="rounded-xl border border-[var(--border)] overflow-hidden" data-testid={`playlist-item-${i}`}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : i)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/4 transition-colors text-left"
                    data-testid={`playlist-expand-${i}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#D946EF]/15 flex items-center justify-center flex-shrink-0">
                      <BookOpen size={14} className="text-[#D946EF]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold font-[Outfit] truncate">{pl.name}</p>
                      <p className="text-zinc-500 text-xs font-[Manrope]">
                        {(pl.songs || []).length} songs • by {pl.created_by || 'Unknown'}
                      </p>
                    </div>
                    <motion.div animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
                      <ChevronRight size={14} className="text-zinc-600" />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-3 space-y-1 max-h-40 overflow-y-auto border-t border-[var(--border)] pt-2">
                          {(pl.songs || []).length === 0 ? (
                            <p className="text-zinc-600 text-xs py-2 text-center">No songs in this playlist</p>
                          ) : (
                            (pl.songs || []).slice(0, 15).map((song, j) => (
                              <div key={j} className="flex items-center gap-2 py-1">
                                <span className="text-zinc-700 text-xs font-mono-stats w-5 text-right flex-shrink-0">{j + 1}</span>
                                <Music size={10} className="text-zinc-600 flex-shrink-0" />
                                <span className="text-zinc-400 text-xs truncate font-[Manrope]">{song.title || 'Unknown'}</span>
                              </div>
                            ))
                          )}
                          {(pl.songs || []).length > 15 && (
                            <p className="text-zinc-600 text-xs text-center pt-1">
                              +{pl.songs.length - 15} more songs
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Listening Leaderboard ────────────────────────────────────────────────────

const MEDAL_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32'];

function Leaderboard({ guildId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!guildId) return;
    setLoading(true);
    axios.get(`${API}/api/music/leaderboard`, { params: { guild_id: guildId } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [guildId]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5 mb-6"
      data-testid="listening-leaderboard"
    >
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={16} className="text-amber-400" />
        <h3 className="text-sm font-semibold text-white font-[Outfit]">Listening Leaderboard</h3>
      </div>
      <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Most active listeners by total plays</p>

      {loading ? (
        <div className="py-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (data?.leaderboard || []).length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-12">No plays recorded yet</p>
      ) : (
        <div className="space-y-1.5">
          {data.leaderboard.map((entry) => (
            <div
              key={entry.rank}
              data-testid={`leaderboard-row-${entry.rank}`}
              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)] transition-colors"
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold font-mono-stats flex-shrink-0"
                style={{
                  background: entry.rank <= 3 ? `${MEDAL_COLORS[entry.rank - 1]}22` : 'rgba(255,255,255,0.05)',
                  color: entry.rank <= 3 ? MEDAL_COLORS[entry.rank - 1] : '#71717A',
                }}
              >
                {entry.rank}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium font-[Manrope] truncate">{entry.name}</p>
                {entry.top_song && <p className="text-[11px] text-zinc-500 truncate">Top: {entry.top_song}</p>}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-mono-stats font-bold text-[#5865F2]">{entry.plays}</p>
                <p className="text-[10px] text-zinc-600 uppercase tracking-wider">plays</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

// ─── Music History Search ─────────────────────────────────────────────────────

function MusicHistorySearch({ guildId }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const runSearch = (q) => {
    if (!q.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    axios.get(`${API}/api/music/search`, { params: { guild_id: guildId, q: q.trim() } })
      .then(r => setResults(r.data.results || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  };

  const handleChange = (value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 350);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5 mb-6"
      data-testid="music-history-search"
    >
      <div className="flex items-center gap-2 mb-1">
        <Search size={16} className="text-[#5865F2]" />
        <h3 className="text-sm font-semibold text-white font-[Outfit]">Search Play History</h3>
      </div>
      <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Find songs by title or who requested them</p>

      <input
        value={query}
        onChange={e => handleChange(e.target.value)}
        placeholder="Search by song title or username..."
        data-testid="music-search-input"
        className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 focus:bg-white/8 transition-all font-[Manrope]"
      />

      {loading && (
        <div className="py-6 flex justify-center">
          <div className="w-5 h-5 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && results && (
        results.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-6">No matches found</p>
        ) : (
          <div className="mt-3 space-y-1 max-h-72 overflow-y-auto" data-testid="music-search-results">
            {results.map((r, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--bg-hover)]" data-testid={`music-search-result-${i}`}>
                <div className="w-8 h-8 bg-[#5865F2]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Music size={13} className="text-[#5865F2]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-[Manrope] truncate">{r.title}</p>
                  <p className="text-xs text-zinc-500">by {r.username}</p>
                </div>
                <p className="text-xs text-zinc-600 font-mono-stats flex-shrink-0">{new Date(r.timestamp).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )
      )}
    </motion.div>
  );
}

function FavoritesPanel({ guildId }) {
  const [favorites, setFavorites] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [queueing, setQueueing] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(() => {
    const token = localStorage.getItem('discord_token');
    axios.get(`${API}/api/favorites`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setFavorites(r.data.favorites || []))
      .catch(() => setFavorites([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (url) => {
    setRemoving(url);
    const token = localStorage.getItem('discord_token');
    try {
      await axios.delete(`${API}/api/favorites`, { params: { url }, headers: { Authorization: `Bearer ${token}` } });
      setFavorites(prev => prev.filter(f => f.url !== url));
    } catch {
      // ignore
    } finally {
      setRemoving(null);
    }
  };

  const queue = async (fav) => {
    setQueueing(fav.url);
    const token = localStorage.getItem('discord_token');
    try {
      await axios.post(`${API}/api/music/request`, { query: fav.url, guild_id: guildId }, { headers: { Authorization: `Bearer ${token}` } });
      setFeedback({ url: fav.url, ok: true });
    } catch (e) {
      setFeedback({ url: fav.url, ok: false, msg: e.response?.data?.detail });
    } finally {
      setQueueing(null);
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  if (favorites === null || favorites.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl mb-6"
      data-testid="favorites-panel"
    >
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[var(--border)]">
        <Star size={15} className="text-amber-400" />
        <span className="text-sm font-semibold text-white font-[Outfit]">My Favorites</span>
        <span className="text-xs text-zinc-600 font-[Manrope] ml-1">— saved with the ⭐ button in Discord</span>
      </div>
      <div className="p-3 space-y-1.5 max-h-72 overflow-y-auto">
        {favorites.map(f => (
          <div key={f.url} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--bg-hover)] transition-colors" data-testid={`favorite-${f.url}`}>
            {f.thumbnail ? <img src={f.thumbnail} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" /> : <div className="w-9 h-9 rounded-lg bg-[#5865F2]/20 flex items-center justify-center flex-shrink-0"><Music size={14} className="text-[#5865F2]" /></div>}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate font-[Manrope]">{f.title}</p>
              <p className="text-xs text-zinc-500 truncate">{f.uploader}</p>
            </div>
            {feedback?.url === f.url && (
              <span className={`text-xs ${feedback.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                {feedback.ok ? 'Queued!' : (feedback.msg || 'Failed')}
              </span>
            )}
            <button
              onClick={() => queue(f)}
              disabled={queueing === f.url}
              title="Add to queue"
              data-testid={`queue-favorite-${f.url}`}
              className="p-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-[#5865F2] transition-colors disabled:opacity-40"
            >
              {queueing === f.url ? <div className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin" /> : <PlusCircle size={14} />}
            </button>
            <button
              onClick={() => remove(f.url)}
              disabled={removing === f.url}
              title="Remove from favorites"
              data-testid={`remove-favorite-${f.url}`}
              className="p-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-red-400 transition-colors disabled:opacity-40"
            >
              {removing === f.url ? <div className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin" /> : <X size={14} />}
            </button>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function WidgetPromoCard({ guildId }) {
  const [copied, setCopied] = useState(false);
  const widgetUrl = `${window.location.origin}/widget/${guildId}`;
  const iframeCode = `<iframe src="${widgetUrl}" width="380" height="140" frameborder="0" style="border-radius:12px;overflow:hidden;"></iframe>`;

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  if (!guildId) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5 mb-6"
      data-testid="widget-promo-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <ExternalLink size={16} className="text-[#5865F2]" />
        <h3 className="text-sm font-semibold text-white font-[Outfit]">Embeddable Now-Playing Widget</h3>
      </div>
      <p className="text-xs text-zinc-500 font-[Manrope] mb-3">
        Embed the live now-playing widget on your website or stream overlay. Append <code className="text-zinc-300">?theme=light</code> for a light variant.
      </p>
      <div className="flex items-center gap-2 bg-[var(--bg-hover)] border border-[var(--border)] rounded-lg px-3 py-2 mb-2">
        <code className="flex-1 text-xs text-zinc-300 truncate font-mono">{widgetUrl}</code>
        <a href={widgetUrl} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-[#5865F2] transition-colors" title="Open widget">
          <ExternalLink size={13} />
        </a>
      </div>
      <div className="flex items-center gap-2 bg-[var(--bg-hover)] border border-[var(--border)] rounded-lg px-3 py-2">
        <code className="flex-1 text-xs text-zinc-400 truncate font-mono">{iframeCode}</code>
        <button onClick={() => copy(iframeCode)} title="Copy embed code" className="text-zinc-500 hover:text-[#5865F2] transition-colors flex-shrink-0" data-testid="copy-iframe-btn">
          {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
        </button>
      </div>
    </motion.div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const MUSIC_BG = "https://images.pexels.com/photos/27141312/pexels-photo-27141312.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function MusicStats() {
  const { selectedGuildId } = useGuild();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroNowPlaying, setHeroNowPlaying] = useState(null);

  useEffect(() => {
    if (!selectedGuildId) return;
    setLoading(true);
    axios.get(`${API}/api/music/stats`, { params: { guild_id: selectedGuildId } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedGuildId]);

  useEffect(() => {
    if (!selectedGuildId) return;
    const fetchNowPlaying = () => {
      axios.get(`${API}/api/music/now-playing`)
        .then(r => {
          const entry = (r.data?.playing || []).find(p => p.guild_id === selectedGuildId);
          setHeroNowPlaying(entry || null);
        })
        .catch(() => {});
    };
    fetchNowPlaying();
    const iv = setInterval(fetchNowPlaying, 5000);
    return () => clearInterval(iv);
  }, [selectedGuildId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Sidebar />
        <main className="lg:ml-60 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="music-stats-main">
        {/* Header */}
        <div className="mb-8 flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-[#5865F2] to-[#D946EF] rounded-xl flex items-center justify-center">
            <Music size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold font-[Outfit] text-white">Music Stats</h1>
            <p className="text-zinc-500 text-sm font-[Manrope]">Live queue, playlists & playback analytics</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard icon={Music} label="Total Plays" value={(data?.total_plays || 0).toLocaleString()} sub="All time" accent="#5865F2" />
          <StatCard icon={Headphones} label="Unique Songs" value={(data?.unique_songs || 0).toLocaleString()} sub="Different tracks" accent="#D946EF" />
          <StatCard icon={Clock} label="Top Hour" value={data?.hourly_chart?.reduce((max, h) => h.plays > (max?.plays || 0) ? h : max, null)?.hour || '—'} sub="Most active time" accent="#10B981" />
        </div>

        {/* Now Playing Hero */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative rounded-2xl overflow-hidden mb-6 border border-[var(--border-md)]"
          style={{ backgroundImage: `url(${MUSIC_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          data-testid="music-hero"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent" />
          <div className="relative p-6 flex items-center gap-5">
            <Waveform active={!!heroNowPlaying} paused={!!heroNowPlaying?.is_paused} />
            {heroNowPlaying ? (
              <div>
                <p className="text-xs text-emerald-400 uppercase tracking-widest mb-1 font-[Manrope] flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${heroNowPlaying.is_paused ? 'bg-amber-400' : 'bg-emerald-400 animate-pulse'}`} />
                  {heroNowPlaying.is_paused ? 'Paused' : 'Now Playing'}
                </p>
                <p className="text-xl font-bold font-[Outfit] text-white" data-testid="hero-now-playing-title">
                  {heroNowPlaying.title}
                </p>
                <p className="text-zinc-400 text-sm">{heroNowPlaying.uploader || ''}</p>
              </div>
            ) : (
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-widest mb-1 font-[Manrope]">Most Played</p>
                <p className="text-xl font-bold font-[Outfit] text-white">
                  {data?.top_songs?.[0]?.name || 'No songs yet'}
                </p>
                <p className="text-zinc-400 text-sm">{data?.top_songs?.[0]?.plays || 0} plays • {data?.top_songs?.[0]?.uploader || ''}</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Join Voice Channel */}
        <JoinChannelForm guildId={selectedGuildId} />

        {/* Song Request Form */}
        <SongRequestForm guildId={selectedGuildId} />
        <FavoritesPanel guildId={selectedGuildId} />
        <WidgetPromoCard guildId={selectedGuildId} />

        {/* Live Queue + Playlist Browser */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <QueueViewer />
          <PlaylistBrowser guildId={selectedGuildId} />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Top Songs */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="lg:col-span-2 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="top-songs-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Top 10 Songs</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Most requested tracks</p>
            {(data?.top_songs || []).length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-12">No plays recorded yet. Use /play in Discord!</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={(data?.top_songs || []).slice(0, 8)} layout="vertical">
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fill: '#A1A1AA', fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="plays" name="Plays" fill="#5865F2" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>

          {/* Top Requesters */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="top-requesters-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Top Requesters</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Most active listeners</p>
            {(data?.top_requesters || []).length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-12">No data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data.top_requesters} dataKey="plays" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                    {data.top_requesters.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={({ active, payload }) => active && payload?.[0] ? (
                    <div className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-xs">
                      <p className="text-zinc-400">{payload[0].name}</p>
                      <p className="text-white font-bold">{payload[0].value} plays</p>
                    </div>
                  ) : null} />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="mt-2 space-y-1">
              {(data?.top_requesters || []).slice(0, 4).map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="text-zinc-400 truncate flex-1">{r.name}</span>
                  <span className="text-zinc-300 font-mono-stats">{r.plays}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Hourly + Daily Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="hourly-plays-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Plays by Hour</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Music activity throughout the day</p>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={data?.hourly_chart || []}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="hour" tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} interval={3} />
                <YAxis tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="plays" name="Plays" fill="#D946EF" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="daily-plays-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Daily Plays</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Last 14 days</p>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={data?.daily_plays || []}>
                <defs>
                  <linearGradient id="playsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#5865F2" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#5865F2" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} interval={2} />
                <YAxis tick={{ fill: '#71717A', fontSize: 9 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="plays" name="Plays" stroke="#5865F2" fill="url(#playsGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Listening Leaderboard */}
        <Leaderboard guildId={selectedGuildId} />

        {/* Search Play History */}
        <MusicHistorySearch guildId={selectedGuildId} />

        {/* Recent Plays Table */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden"
          data-testid="recent-plays-table"
        >
          <div className="px-5 py-4 border-b border-[var(--border)]">
            <h3 className="text-sm font-semibold text-white font-[Outfit]">Recent Plays</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  <th className="text-left px-5 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider">Song</th>
                  <th className="text-left px-5 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider hidden sm:table-cell">Requested By</th>
                  <th className="text-left px-5 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider hidden md:table-cell">Duration</th>
                  <th className="text-left px-5 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider">Time</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_plays || []).length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-8 text-center text-zinc-500">No plays recorded yet</td></tr>
                ) : (
                  (data?.recent_plays || []).map((p, i) => (
                    <tr key={i} className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#5865F2]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Music size={13} className="text-[#5865F2]" />
                          </div>
                          <span className="text-white font-[Manrope] truncate max-w-[180px]">{p.title}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-zinc-400 hidden sm:table-cell">{p.username}</td>
                      <td className="px-5 py-3 text-zinc-400 font-mono-stats hidden md:table-cell">
                        {p.duration ? `${Math.floor(p.duration / 60)}:${String(p.duration % 60).padStart(2, '0')}` : '—'}
                      </td>
                      <td className="px-5 py-3 text-zinc-500 font-mono-stats text-xs">
                        {new Date(p.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
